import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { useCart } from "../components/CartContext";
import CheckoutForm from "../components/StripeCheckoutForm";

import "../styles/Cart.css";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

export default function Cart() {
  const { cartItems, addToCart, removeOneFromCart, removeFromCart, clearCart } =
    useCart();
  const [productDetails, setProductDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);

  const [status, setStatus] = useState(null);
  const [customerEmail, setCustomerEmail] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [stripeSessionId, setStripeSessionId] = useState("");
  const [stripePaymentId, setStripePaymentId] = useState("");

  const hasFetchedSession = useRef(false);
  console.log("For testing purposes only");
  useEffect(() => {
    // If already hit backedn don't hit again
    if (hasFetchedSession.current) return;
    hasFetchedSession.current = true;

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const sessionId = urlParams.get("session_id");

    if (sessionId) {

      fetch(`${apiUrl}/stripe/session-status?session_id=${sessionId}`, {
        headers: { "X-App-Secret": process.env.REACT_APP_APP_SECRET },
      })
        .then((res) => res.json())
        .then((data) => {
          setStatus(data.status);
          setCustomerEmail(data.customer_email);
          setOrderNumber(data.order_number);
          setStripeSessionId(data.session_id);
          setStripePaymentId(data.payment_id);
          if (data.status === "complete") {
            clearCart(); // Clear cart if payment is successful
          }
          // Fire Google Ads Purchase Conversion Event
          if (window.gtag) {

            window.gtag('event', 'conversion', {
              send_to: 'AW-17010350762/NAbJCNqUsr8aEKq1la8_', // your Google Ads ID
              transaction_id: data.order_number || '' // optional, if you have order number
            });
          }
          // ✅ TikTok direct pixel (optional fallback)
          if (window.ttq) {
            window.ttq.track('Purchase', {
              value: data.amount_total / 100,
              currency: 'GBP',
              order_id: data.order_number
            });
          }
        
          // ✅ GTM Data Layer push (REQUIRED for TikTok via GTM)
      
            const items = (data.order || []).map((item) => ({
              item_id: item.product_id,
              item_name: item.name,
              item_category: "Apparel",
              quantity: item.quantity,
              size: item.size,
              price: Number(item.price) || 0 
            }));

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "purchase",
              content_type: "product", // for Meta Pixel
              contents: items.map(item => ({
                id: item.item_id,
                quantity: item.quantity
              })), // for Meta Pixel
              ecommerce: {
                value: data.amount_total / 100,
                currency: "GBP",
                transaction_id: data.order_number,
                items: items
              }
            });
        });
    }
  }, []);

  useEffect(() => {
    if (cartItems.length > 0) {
      const fetchProductDetails = async () => {
        try {
          setLoading(true);
          const itemIds = cartItems.map((item) => item.id);

          const body = { productIds: itemIds };

          const response = await fetch(`${apiUrl}/ct/products/batch`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-App-Secret": process.env.REACT_APP_APP_SECRET,
            },
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            throw new Error("Failed to fetch product details");
          }

          const products = await response.json();
          setProductDetails(products);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchProductDetails();
    } else {
      setProductDetails([]);
      setLoading(false);
    }
  }, [cartItems]);

  const handleAddToCart = (itemId, itemSize) => {
    addToCart(itemId, itemSize);
  };

  const handleRemoveOneFromCart = (id, size) => {
    removeOneFromCart(id, size);
  };

  const handleRemoveFromCart = (id, size) => {
    removeFromCart(id, size);
  };

  const totalPrice = cartItems.reduce((total, item) => {
    const product = productDetails.find((p) => p.product_id === item.id);
    return product ? total + product.price * item.quantity : total;
  }, 0);

  if (error) {
    return <div>Error: {error}</div>;
  }

  const handleCheckout = async () => {
    if (cartItems.length > 0) {
      setShowCheckoutForm(true);
    } else {
      alert("Please add something to your cart before checking out");
    }
  };

  return (
    <div
      className="cart-page"
      style={{
        cursor: loading ? "progress" : "default",
      }}
    >

      <div className={`cart-container`}>
        <h1 className="cart-header nn">Shopping Bag:</h1>
        {status === "complete" && (
          <section id="success" stripesession={stripeSessionId}>
            <p>
              Thank you for your purchase!
              <br />
              Your order <strong>#{orderNumber}</strong> has been successfully
              processed.
              <br />
              Your payment was received (Payment ID:{" "}
              <strong>{stripePaymentId}</strong>).
              <br />
              Please keep your order number for any future inquiries. We will
              send a confirmation email shortly to{" "}
              <strong>{customerEmail}</strong>.
              <br />
            </p>
          </section>
        )}
        {cartItems.length > 0 ? (
          cartItems.map((item) => {
            const product = productDetails.find(
              (p) =>
                p.product_id === item.id &&
                p.sizes.some((s) => s.size === item.size && s.inStock)
            );
            return product !== undefined ? (
              <div
                key={`${product.product_id}-${item.size}`}
                className="cart-item"
              >
                <Link to={`/shop/${product.product_id}`}>
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="cart-item-image"
                  />
                </Link>
                <div className="cart-item-info">
                  <div className="cart-item-header">
                    <div className="cart-item-title">{product.name}</div>
                    <div
                      className={`cart-clickable cart-delete ${showCheckoutForm ? "disabled-buttons" : ""
                        }`}
                      onClick={() =>
                        handleRemoveFromCart(product.product_id, item.size)
                      }
                    >
                      &times;
                    </div>
                  </div>
                  <div className="cart-item-details">
                    {/* <p>Colour: {item.color || "N/A"}</p> */}
                    <p>Size: {item.size}</p>
                  </div>
                  <div className="cart-item-quantity">
                    <div
                      className={`cart-button left ${item.quantity < 2 ? "disabled" : ""
                        } ${showCheckoutForm ? "disabled-buttons" : ""}`}
                      onClick={() =>
                        handleRemoveOneFromCart(product.product_id, item.size)
                      }
                    >
                      -
                    </div>
                    <div
                      className={`cart-item-quantity-container ${showCheckoutForm ? "disabled-buttons" : ""
                        }`}
                    >
                      <span>QTY: {item.quantity}</span>
                    </div>
                    <div
                      className={`cart-button right ${showCheckoutForm ? "disabled-buttons" : ""
                        }`}
                      onClick={() =>
                        handleAddToCart(product.product_id, item.size)
                      }
                    >
                      +
                    </div>
                  </div>
                  <div className="cart-item-price">£{product.price}</div>
                </div>
              </div>
            ) : null;
          })
        ) : (
          <div className="cart-empty">Your cart is empty.</div>
        )}
      </div>
      <div className="cart-summary">
        <h2>Order Summary</h2>
        <div className="summary-details">
          <div className="summary-line">
            <span>Subtotal</span>
            <span>£{totalPrice.toFixed(2)}</span>
          </div>
          {totalPrice > 80 ? (
            <div className="summary-line">
              <span>Standard Delivery</span>
              <span>Free</span>
            </div>
          ) : (
            <div className="summary-line">
              <span>Standard Delivery</span>
              <span>£4.99</span>
            </div>
          )}
          <div className="summary-line">
            <span>Estimated Total</span>
            <span>
              £{(totalPrice + (totalPrice > 80 ? 0 : 4.99)).toFixed(2)}
            </span>
          </div>
        </div>
        {showCheckoutForm ? (
          <CheckoutForm cartItems={cartItems} />
        ) : (
          <div
            className="checkout-button cart-clickable"
            onClick={handleCheckout}
          >
            Proceed to Checkout
          </div>
        )}
      </div>
    </div>
  );
}
